
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TermsModal } from './TermsModal';
import { CPFInput } from './CPFInput';
import { useStore } from '@/hooks/use-store';

const storeSchema = z.object({
  store_name: z.string().min(1, 'Nome da loja é obrigatório').max(100),
  cpf: z.string().min(14, 'CPF inválido').max(14),
  full_name: z.string().min(1, 'Nome completo é obrigatório').max(150),
  contact_email: z.string().email('Email inválido'),
  terms_accepted: z.boolean().refine(val => val === true, 'Você deve aceitar os termos'),
});

type StoreFormData = z.infer<typeof storeSchema>;

interface StoreRegistrationProps {
  onSuccess: () => void;
}

export const StoreRegistration = ({ onSuccess }: StoreRegistrationProps) => {
  const [showTerms, setShowTerms] = useState(false);
  const { createStore } = useStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<StoreFormData>({
    resolver: zodResolver(storeSchema),
  });

  const onSubmit = async (data: StoreFormData) => {
    try {
      await createStore({
        store_name: data.store_name,
        cpf: data.cpf,
        full_name: data.full_name,
        contact_email: data.contact_email,
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating store:', error);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>Cadastro da Loja</CardTitle>
          <CardDescription>
            Crie sua loja gratuita e comece a vender seus designs personalizados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="store_name">Nome da Loja *</Label>
              <Input
                id="store_name"
                {...register('store_name')}
                placeholder="Ex: Design Studio da Ana"
              />
              {errors.store_name && (
                <p className="text-sm text-destructive">{errors.store_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cpf">CPF *</Label>
              <CPFInput
                value={watch('cpf') || ''}
                onChange={(value) => setValue('cpf', value)}
                error={errors.cpf?.message}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="full_name">Nome Completo *</Label>
              <Input
                id="full_name"
                {...register('full_name')}
                placeholder="Seu nome completo"
              />
              {errors.full_name && (
                <p className="text-sm text-destructive">{errors.full_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email">E-mail de Contato *</Label>
              <Input
                id="contact_email"
                type="email"
                {...register('contact_email')}
                placeholder="seu@email.com"
              />
              {errors.contact_email && (
                <p className="text-sm text-destructive">{errors.contact_email.message}</p>
              )}
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={watch('terms_accepted') || false}
                onCheckedChange={(checked) => setValue('terms_accepted', checked as boolean)}
              />
              <div className="space-y-1 leading-none">
                <Label htmlFor="terms" className="text-sm">
                  Aceito os{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="text-primary underline hover:no-underline"
                  >
                    termos e condições
                  </button>{' '}
                  *
                </Label>
                {errors.terms_accepted && (
                  <p className="text-sm text-destructive">{errors.terms_accepted.message}</p>
                )}
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Criando loja...' : 'Criar Minha Loja'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <TermsModal open={showTerms} onOpenChange={setShowTerms} />
    </div>
  );
};
